import { useTheme } from '@emotion/react';
import Icon_Close from 'assets/Cat-Entity/Icon_Form_Search_02.png';
import EmptyPanel from 'assets/ui-update/empty-side-01.svg';
import { FoldingCube } from 'better-react-spinkit';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GeneralError } from '../../../components/GeneralError/GeneralError';
import Button from '../../../components/button/Button';
import { ModalTypes, closeModal, openModal } from '../../../reducers/modal';
import { updatePayeeSubpage } from '../../../reducers/payee';
import { MoveMoneySubPages, SavedPayee } from '../MoveMoney';
import styles from './PayeePanel.module.scss';
import { endpoints } from '../../../endpoints.config';
import { ApiResponse, useFetch } from '../../../api';
import { PayeeType, TransmitterType } from '../Payees/EditPayee';
import { useIsFinancialInstitution } from '../Payees/helpers';
import { Collapse } from '../../../components/Collapse/Collapse';
import { format } from 'date-fns';

export type PayeeInfo = {
    accountName: string;
    accountType: 'Card' | string;
    achAccountNumber: string;
    achRoutingNumber: string;
    countryName: string;
    iban: string;
    id: number;
    name: string;
    swiftNumber: string;
    intermediaryBic?: string;
    type: PayeeType;
    bSuppressed: boolean; // awaiting approval - can't make payments while true
    cardLast4Digits?: string;
    cardCompany?: string;

    // transmitter info
    transmitterFirstPartyTransfer: boolean;
    transmitterType: TransmitterType;
    transmitterName: string;
    transmitterDateOfBirth?: string;
    transmitterAccountNumber: string;
    transmitterAddressLine1: string;
    transmitterAddressLine2: string;
    transmitterTownCity: string;
    transmitterState: string;
    transmitterPostcode: string;
    transmitterCountryCode: string;
};

type Props = {
    selectedRowData?: SavedPayee;
    disablePayment?: boolean;
    closePanel: () => void;
};

export const PayeePanel: React.FC<Props> = ({
    selectedRowData: rowData,
    closePanel,
    disablePayment,
}) => {
    const dispatch = useDispatch();
    const bFinancialInstitution = useIsFinancialInstitution();
    const { t } = useTranslation(['moveMoney', 'common']);

    const { colors } = useTheme();

    const { data, loading, error } = useFetch<ApiResponse<PayeeInfo>>(
        endpoints.accounts.payeeDetails,
        {
            params: { id: rowData?.payees__Id + '' },
        },
        undefined,
        !rowData
    );

    if (!rowData) return <EmptySelection />;

    const handleMakePayment = () => {
        dispatch(
            updatePayeeSubpage({
                page: MoveMoneySubPages.SAVED_PAYEE,
                payee: rowData,
            })
        );
        closePanel();
    };
    const handleEditPayee = () => {
        dispatch(
            updatePayeeSubpage({
                page: MoveMoneySubPages.EDIT_PAYEE,
                payee: rowData,
            })
        );
        dispatch(closeModal());
        closePanel();
    };

    const handleRemovePayee = () => {
        closePanel();
        dispatch(
            openModal({
                modalType: ModalTypes.REMOVE_PAYEE,
                data: rowData,
            })
        );
    };

    const isCardPayee = data?.details.accountType === 'Card';
    return (
        <div key={rowData?.payees__Id} className={styles.Panel}>
            <div className={styles.Header}>
                <h2 className={styles.Title} translate='no'>{rowData.payees__Name}</h2>
                <img
                    onClick={closePanel}
                    className={styles.CloseIcon}
                    src={Icon_Close}
                    alt="Close modal"
                />
            </div>

            {loading && (
                <div className={styles.Loading}>
                    <FoldingCube color={colors.first} size={80} />
                </div>
            )}
            {error && !loading && (
                <div className={styles.Error}>
                    <GeneralError message={t('unableToLoadPayee')} />
                </div>
            )}
            {data && !loading && (
                <div className={styles.Details}>
                    {data.details.bSuppressed && (
                        <TransactionInfoItem
                            label={t('status')}
                            value={t('pendingApproval')}
                            valueClassname={styles.ApprovalPending}
                        />
                    )}
                    <TransactionInfoItem
                        label={t('accountType')}
                        value={
                            isCardPayee ? data.details.accountType : t(`payeeType_${data.details.type}`)
                        }
                    />

                    <TransactionInfoItem
                        label={t('accountCountry')}
                        value={data.details.countryName}
                    />
                    <TransactionInfoItem label={t('payeeName')} value={data.details.name} />
                    {!isCardPayee && (
                        <TransactionInfoItem
                            label={t('accountName')}
                            value={data.details.accountName}
                        />
                    )}
                    {isCardPayee && (
                        <>
                            {data.details.cardLast4Digits && (
                                <TransactionInfoItem
                                    label={t('cardNumber')}
                                    value={`XXXX XXXX XXXX ${data.details.cardLast4Digits}`}
                                />
                            )}
                            {data.details.cardCompany && (
                                <TransactionInfoItem
                                    label={t('cardProvider')}
                                    value={data.details.cardCompany}
                                />
                            )}
                        </>
                    )}
                    {data.details.achAccountNumber && (
                        <TransactionInfoItem
                            label={t('accountNumber')}
                            value={data.details.achAccountNumber}
                        />
                    )}
                    {data.details.achRoutingNumber && (
                        <TransactionInfoItem
                            label={t('routingNumber')}
                            value={data.details.achRoutingNumber}
                        />
                    )}
                    {data.details.iban && (
                        <TransactionInfoItem label={t('ibanNumber')} value={data.details.iban} />
                    )}
                    {data.details.swiftNumber && (
                        <TransactionInfoItem
                            label={t('swiftNumber')}
                            value={data.details.swiftNumber}
                        />
                    )}
                    {data.details.intermediaryBic && (
                        <TransactionInfoItem
                            label={t('intermediarySwift')}
                            value={data.details.intermediaryBic}
                        />
                    )}
                    {bFinancialInstitution && (
                        <>
                            <TransactionInfoItem
                                label={t('firstPartyTransfer')}
                                value={data.details.transmitterFirstPartyTransfer ? t('common:yes') : t('common:no')}
                            />
                            {!data.details.transmitterFirstPartyTransfer && (
                                <Collapse header={t('transmitterInfo')}>
                                    <TransactionInfoItem
                                        label={t('transmitterTypeLabel')}
                                        value={data.details.transmitterType}
                                    />
                                    <TransactionInfoItem
                                        label={t('transmitterNameLabel')}
                                        value={data.details.transmitterName}
                                    />
                                    {data.details.transmitterType === TransmitterType.Personal && (
                                        <TransactionInfoItem
                                            label={t('dateOfBirth')}
                                            value={
                                                data.details.transmitterDateOfBirth &&
                                                Date.parse(data.details.transmitterDateOfBirth)
                                                    ? format(
                                                          new Date(
                                                              data.details.transmitterDateOfBirth
                                                          ),
                                                          'dd/MM/yyyy'
                                                      )
                                                    : data.details.transmitterDateOfBirth
                                            }
                                        />
                                    )}
                                    {data.details.transmitterAccountNumber && (
                                        <TransactionInfoItem
                                            label={t('accountNumber')}
                                            value={data.details.transmitterAccountNumber}
                                        />
                                    )}
                                    <TransactionInfoItem
                                        label={t('addressLine1')}
                                        value={data.details.transmitterAddressLine1}
                                    />
                                    {data.details.transmitterAddressLine2 && (
                                        <TransactionInfoItem
                                            label={t('addressLine2')}
                                            value={data.details.transmitterAddressLine2}
                                        />
                                    )}
                                    <TransactionInfoItem
                                        label={t('townCity')}
                                        value={data.details.transmitterTownCity}
                                    />
                                    <TransactionInfoItem
                                        label={t('state')}
                                        value={data.details.transmitterState}
                                    />
                                    <TransactionInfoItem
                                        label={t('zipCode')}
                                        value={data.details.transmitterPostcode}
                                    />
                                    <TransactionInfoItem
                                        label={t('country')}
                                        value={data.details.transmitterCountryCode}
                                    />
                                </Collapse>
                            )}
                        </>
                    )}
                </div>
            )}
            {data && !loading && (
                <div className={styles.Buttons}>
                    {!disablePayment && !data?.details.bSuppressed && (
                        <Button onClick={handleMakePayment} color="third">
                            {t('makePayment')}
                        </Button>
                    )}
                    {!isCardPayee && (
                        <Button onClick={handleEditPayee} color="second" priority="secondary">
                            {t('editPayeeDetails')}
                        </Button>
                    )}
                    <Button onClick={handleRemovePayee} color="grey">
                        {t('removePayee')}
                    </Button>
                </div>
            )}
        </div>
    );
};

type InfoItemProps = {
    label: string;
    value?: string;
    valueClassname?: string;
};

const TransactionInfoItem: React.FC<InfoItemProps> = ({ label, value, valueClassname = '' }) => (
    <div className={styles.InfoItem}>
        <p className={styles.Label}>{label}:</p>
        <p className={`${styles.Value} ${valueClassname} notranslate`} translate="no">{value ?? '-'}</p>
    </div>
);

const EmptySelection = () => {
    const { t } = useTranslation('moveMoney');
    return (
        <div className={`${styles.Panel} ${styles.Empty}`}>
            <img alt="Empty" src={EmptyPanel} />
            <p>{t('noPayeeSelected')}</p>
        </div>
    );
};
